import React, { useEffect, useState } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { Layout } from './components/layout/Layout'
import {
  CreditCardIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  CogIcon
} from '@heroicons/react/24/outline'
import { Dashboard } from './components/dashboard/Dashboard'
import LoginPage from '@/pages/auth/LoginPage'
import { useAuthStore } from '@/stores/authStore'
import TransactionsList from '@/pages/transactions/TransactionsList'
// SaaS Pages
import LandingPage from '@/pages/saas/LandingPage'
import SignupPage from '@/pages/saas/SignupPage'
import OnboardingPage from '@/pages/saas/OnboardingPage'
import BillingPage from '@/pages/saas/BillingPage'
import AccountsList from '@/pages/accounts/AccountsList'
import AccountStatement from '@/pages/accounts/AccountStatement'
import AccountsDashboard from '@/pages/accounts/AccountsDashboard'
import CategoriesList from '@/pages/categories/CategoriesList'
import AccountingDashboard from '@/pages/accounting/AccountingDashboard'
import AIInsights from '@/pages/ai/AIInsights'
import CashFlowPredictions from '@/pages/ai/CashFlowPredictions'
import AIPlayground from '@/pages/ai/AIPlayground'
import CRMDashboard from '@/pages/crm/CRMDashboard'
import CRMCustomers from '@/pages/crm/CRMCustomers'
import CRMCustomerDetails from '@/pages/crm/CRMCustomerDetails'
import CRMAnalytics from '@/pages/crm/CRMAnalytics'
import CryptoDashboard from '@/pages/crypto/CryptoDashboard'
import PaymentGatewayDashboard from '@/pages/payments/PaymentGatewayDashboard'
import PIXManager from '@/pages/payments/PIXManager'
import PIXTransferPage from '@/pages/payments/PIXTransferPage'
import BankReconciliation from '@/pages/payments/BankReconciliation'
import ReportsPage from '@/pages/reports/ReportsPage'
import BillsPage from '@/pages/bills/BillsPage'
import CostCentersPage from '@/pages/cost-centers/CostCentersPage'
import ProjectsPage from '@/pages/projects/ProjectsPage'
import CashFlowPage from '@/pages/cash-flow/CashFlowPage'
import IntegrationsPage from '@/pages/integrations/IntegrationsPage'
import ChatbotPage from '@/pages/chatbot/ChatbotPage'
import FinanceiroDashboard from '@/pages/financeiro/FinanceiroDashboard'
import ContasReceberPage from '@/pages/financeiro/ContasReceberPage'
import ContasReceberDashboard from '@/pages/financeiro/ContasReceberDashboard'
import ContasReceberDetalhes from '@/pages/financeiro/ContasReceberDetalhes'
import TransferenciasPage from '@/pages/financeiro/TransferenciasPage'
import OFXUploadPage from '@/pages/imports/OFXUploadPage'
import ImportsDashboardPage from '@/pages/imports/ImportsDashboardPage'
import BankStatementsPage from '@/pages/bank-statements/BankStatementsPage'
import PluggyDashboard from '@/pages/integrations/PluggyDashboard'
import AsaasDashboard from '@/pages/integrations/AsaasDashboard'
import PagarmeDashboard from '@/pages/integrations/PagarmeDashboard'
import GatewayFinProDashboard from '@/pages/payments/GatewayFinProDashboard'
import GatewayFinProChargeDetails from '@/pages/payments/GatewayFinProChargeDetails'
import GatewayFinProTransfers from '@/pages/payments/GatewayFinProTransfers'
import GatewayFinProSettings from '@/pages/payments/GatewayFinProSettings'
import ProdutosPage from '@/pages/produtos/ProdutosPage'
import SalesPage from '@/pages/sales/SalesPage'
import SellersPage from '@/pages/sales/SellersPage'
import CommissionsPage from '@/pages/sales/CommissionsPage'
import UsersPage from '@/pages/settings/UsersPage'
import InvoiceOCRPage from '@/pages/invoices/InvoiceOCRPage'
import SmartReconciliationPage from '@/pages/reconciliation/SmartReconciliationPage'

function App() {
  const { token, isLoading } = useAuthStore()
  const location = useLocation()

  // Public routes that don't require authentication
  const publicRoutes = ['/landing', '/signup', '/login']
  const isPublicRoute = publicRoutes.includes(location.pathname)

  // Check if user is authenticated
  const isAuthenticated = !!token

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Render public routes without Layout
  if (isPublicRoute || !isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    )
  }

  // Authenticated routes with Layout
  return (
      <Layout>
        <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/transactions" element={<TransactionsList />} />
        <Route path="/accounts" element={<AccountsList />} />
        <Route path="/accounts/:accountId/statement" element={<AccountStatement />} />
        <Route path="/accounts/dashboard" element={<AccountsDashboard />} />
        <Route path="/categories" element={<CategoriesList />} />
        <Route path="/produtos" element={<ProdutosPage />} />
        <Route path="/accounting" element={<AccountingDashboard />} />
        <Route path="/ai" element={<AIInsights />} />
        <Route path="/ai/predictions" element={<CashFlowPredictions />} />
        <Route path="/ai/playground" element={<AIPlayground />} />
        <Route path="/crm" element={<CRMDashboard />} />
        <Route path="/crm/customers" element={<CRMCustomers />} />
        <Route path="/crm/customers/:id" element={<CRMCustomerDetails />} />
        <Route path="/crm/analytics" element={<CRMAnalytics />} />
        <Route path="/crypto" element={<CryptoDashboard />} />
        <Route path="/payments" element={<PaymentGatewayDashboard />} />
        <Route path="/payments/gateway-finpro" element={<GatewayFinProDashboard />} />
        <Route path="/payments/gateway-finpro/charges/:chargeId" element={<GatewayFinProChargeDetails />} />
        <Route path="/payments/gateway-finpro/transfers" element={<GatewayFinProTransfers />} />
        <Route path="/payments/gateway-finpro/settings" element={<GatewayFinProSettings />} />
        <Route path="/payments/pix" element={<PIXManager />} />
        <Route path="/payments/pix-transfer" element={<PIXTransferPage />} />
        {/* Rota antiga removida - agora usa /bank-statements */}
        <Route path="/bills" element={<BillsPage />} />
        <Route path="/invoices/ocr" element={<InvoiceOCRPage />} />
        <Route path="/cost-centers" element={<CostCentersPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/cash-flow" element={<CashFlowPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/integrations/pluggy" element={<PluggyDashboard />} />
        <Route path="/integrations/asaas/:id" element={<AsaasDashboard />} />
        <Route path="/integrations/pagarme/:id" element={<PagarmeDashboard />} />
        <Route path="/chatbot" element={<ChatbotPage />} />
        <Route path="/reconciliation/smart" element={<SmartReconciliationPage />} />
        <Route path="/financeiro/dashboard" element={<FinanceiroDashboard />} />
        <Route path="/financeiro/contas-receber" element={<ContasReceberPage />} />
        <Route path="/financeiro/contas-receber/:contaId/detalhes" element={<ContasReceberDetalhes />} />
        <Route path="/financeiro/contas-receber/dashboard" element={<ContasReceberDashboard />} />
        <Route path="/financeiro/transferencias" element={<TransferenciasPage />} />
        <Route path="/vendas" element={<SalesPage />} />
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/sales/sellers" element={<SellersPage />} />
        <Route path="/sales/commissions" element={<CommissionsPage />} />
        <Route path="/bank-statements" element={<BankStatementsPage />} />
        <Route path="/imports/ofx-upload" element={<OFXUploadPage />} />
        <Route path="/imports/ofx" element={<OFXUploadPage />} />  {/* Rota mais direta */}
        <Route path="/imports/dashboard" element={<ImportsDashboardPage />} />
        <Route path="/cards" element={
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCardIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Gestão de Cartões</h3>
              <p className="text-gray-600 mb-6">Controle seus cartões de crédito e débito, limites e faturas</p>
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
                Conectar ao Backend
              </button>
            </div>
          </div>
        } />
        <Route path="/investments" element={
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ChartBarIcon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Carteira de Investimentos</h3>
              <p className="text-gray-600 mb-6">Acompanhe seus investimentos e rentabilidade</p>
              <button className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700">
                Conectar ao Backend
              </button>
            </div>
          </div>
        } />
        <Route path="/goals" element={
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArrowTrendingUpIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Metas Financeiras</h3>
              <p className="text-gray-600 mb-6">Defina e acompanhe suas metas de economia e investimento</p>
              <button className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700">
                Conectar ao Backend
              </button>
            </div>
          </div>
        } />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/settings/users" element={<UsersPage />} />
        <Route path="/family" element={
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserGroupIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Gestão Familiar</h3>
              <p className="text-gray-600 mb-6">Controle financeiro compartilhado para toda a família</p>
              <button className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700">
                Conectar ao Backend
              </button>
            </div>
          </div>
        } />
        <Route path="/settings" element={
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CogIcon className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Configurações</h3>
              <p className="text-gray-600 mb-6">Personalize sua experiência e configure integrações</p>
              <button className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700">
                Conectar ao Backend
              </button>
            </div>
          </div>
        } />
        <Route path="*" element={<Dashboard />} />
      </Routes>
    </Layout>
  )
}

export default App